/**
 * Public season pages.
 *   /standings      lists the public seasons of this site.
 *   /standings/:id  shows one season's standings.
 * Both read the public season API, which needs no login.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Card, Container, Flex, Heading, Link as RadixLink, Text } from '@radix-ui/themes';
import { describeRule, fmtSeasonDate } from '../../utils/seasons';
import StandingsTable from './StandingsTable';
import {
  fetchPublicSeasons,
  fetchPublicSeasonStandings,
  type Season,
  type SeasonStandingsResponse,
} from './seasonsApi';
import '../Teams/Teams.css';

type PublicSeason = Pick<Season, 'id' | 'name' | 'startDate' | 'endDate' | 'venue'>;

export const PublicSeasonList: React.FC = () => {
  const [seasons, setSeasons] = useState<PublicSeason[] | null>(null);

  useEffect(() => {
    fetchPublicSeasons().then(setSeasons).catch(() => setSeasons([]));
  }, []);

  return (
    <Container size="3" px="4" py="6">
      <Heading size="7" mb="4">Season standings</Heading>
      {seasons === null ? (
        <Text color="gray">Loading…</Text>
      ) : seasons.length === 0 ? (
        <Text color="gray">No seasons to show right now.</Text>
      ) : (
        <Flex direction="column" gap="3">
          {seasons.map((s) => (
            <Card key={s.id} asChild>
              <Link to={`/standings/${s.id}`} style={{ textDecoration: 'none' }}>
                <Text as="div" size="4" weight="bold">{s.name}</Text>
                <Text as="div" size="2" color="gray">
                  {s.venue?.name} · {fmtSeasonDate(s.startDate)} – {fmtSeasonDate(s.endDate)}
                </Text>
              </Link>
            </Card>
          ))}
        </Flex>
      )}
    </Container>
  );
};

const PublicSeasonStandings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<SeasonStandingsResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    let active = true;
    setState('loading');
    fetchPublicSeasonStandings(Number(id))
      .then((d) => {
        if (!active) return;
        setData(d);
        setState('ready');
      })
      .catch(() => {
        if (active) setState('missing');
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (state === 'loading') {
    return <Container size="3" px="4" py="6"><Text color="gray">Loading standings…</Text></Container>;
  }
  if (state === 'missing' || !data) {
    return (
      <Container size="3" px="4" py="6">
        <Heading size="6" mb="2">Season not found</Heading>
        <RadixLink asChild><Link to="/standings">See all seasons</Link></RadixLink>
      </Container>
    );
  }

  const { season, rows, nights, totalNights } = data;
  return (
    <Container size="3" px="4" py="6">
      <Box mb="4">
        <Heading size="7">{season.name}</Heading>
        <Text as="div" size="3" color="gray">
          {season.venue?.name} · {fmtSeasonDate(season.startDate)} – {fmtSeasonDate(season.endDate)}
        </Text>
        <Text as="div" size="2" color="gray" mt="1">
          {describeRule(season)} · {totalNights} game night{totalNights === 1 ? '' : 's'} so far
        </Text>
      </Box>
      {rows.length === 0 ? (
        <Text color="gray">Standings show here after the first game night of the season.</Text>
      ) : (
        <StandingsTable rows={rows} nights={nights} showAttendance={season.attendanceBonus > 0} />
      )}
      <Text as="p" size="1" color="gray" mt="3">Tap a team to see its nights.</Text>
    </Container>
  );
};

export default PublicSeasonStandings;
